import { createContext, PropsWithChildren, useCallback, useContext, useState } from "react";
import VideoPlayer, { VideoPlayerConfig } from "../../components/VideoPlayer";
import { YoutubePlayer } from "../../components/VideoPlayer/types";

type VideoPlayProps = {

} & PropsWithChildren<any>

type VideoParams = {
    name: string,
    url: string,
}

type ContextVideoPlayeData = {
    current: VideoParams,
    changeCurrent: (current: VideoParams) => void,
    list: VideoParams[],
    setVideoList: (params: { name: string, url: string }[]) => void
    play: (name: string, url: string) => void,
    previus: () => void,
    next: () => void,
    hasPrevius: () => boolean,
    hasNext: () => boolean,
    stateChange?: { event: { target: YoutubePlayer, data: number }, current: { url: string } }
    setConfig: (config: VideoPlayerConfig) => void
};

const DEFAULT: ContextVideoPlayeData = {
    current: {} as VideoParams,
    changeCurrent: () => { },
    list: [],
    play: () => { },
    setVideoList: () => [],
    previus: () => { },
    next: () => { },
    hasPrevius: () => false,
    hasNext: () => false,
    stateChange: undefined,
    setConfig: () => { }
}

export const VideoPlayerContext = createContext<ContextVideoPlayeData>(DEFAULT);

export const VideoPlayerProvider = (props: VideoPlayProps) => {
    const [list, setList] = useState<VideoParams[]>([]);
    const [current, setCurrent] = useState<VideoParams>({} as VideoParams);
    const [stateChange, setStateChange] = useState<{ event: { target: YoutubePlayer, data: number }, current: { url: string } }>();
    const [configVideoPlayer, setConfigVideoPlayer] = useState<VideoPlayerConfig>();

    const play = useCallback((name: string, url: string): void => {
        setList(items => {
            if (items.find(i => i.url === url) === undefined) {
                items.push({ name, url });
                return [...items];
            }
            return items;
        });
        setCurrent({ name, url });
    }, [])

    const setVideoList = useCallback((items: { name: string, url: string }[]): void => {
        setList(items);
    }, [])

    const findCurrentIndex = useCallback(() => {
        if (!list || list.length === 0 || !current)
            return;
        return list.findIndex(l => l.url === current.url);
    }, [current, list]);

    const previus = useCallback((): void => {
        const currentIndex = findCurrentIndex();
        if (!!currentIndex && currentIndex > 0) {
            setCurrent(list[currentIndex - 1]);
        }
    }, [findCurrentIndex, list])

    const next = useCallback((): void => {
        const currentIndex = findCurrentIndex();
        if (currentIndex !== undefined && currentIndex < list.length - 1) {
            setCurrent(list[currentIndex + 1]);
        }
    }, [findCurrentIndex, list])

    const hasPrevius = useCallback((): boolean => {
        const currentIndex = findCurrentIndex();
        return !!currentIndex && currentIndex > 0;
    }, [findCurrentIndex]);

    const hasNext = useCallback((): boolean => {
        const currentIndex = findCurrentIndex();
        return currentIndex !== undefined && currentIndex < list.length - 1;
    }, [findCurrentIndex, list]);

    const handleStateChange = useCallback((event: { target: YoutubePlayer, data: number }, current: { url: string }) => {
        setStateChange({ event, current });
    }, []);

    const changeConfigPlayer = useCallback((config: VideoPlayerConfig) => {
        setConfigVideoPlayer(config);
    }, []);

    return (
        <VideoPlayerContext.Provider
            value={{
                current,
                changeCurrent: setCurrent,
                play,
                previus,
                next,
                list,
                setVideoList,
                hasPrevius,
                hasNext,
                stateChange,
                setConfig: changeConfigPlayer
            }} >
            <VideoPlayer config={configVideoPlayer} stateChange={handleStateChange} />
            {props.children}
        </VideoPlayerContext.Provider>
    );
}

export function useVideoPlayer(): ContextVideoPlayeData {
    const context = useContext(VideoPlayerContext);

    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return context;
}